// Listing modes for Groups — List / Matrix.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/core/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/list     → GroupsList    (management grid — CRUD)
//   <base>/matrix   → GroupsMatrix  (read-only modules × groups grant grid,
//                                     GET /v6/groups/matrix)
//
// Record routes (create/:id) are SIBLINGS of the mode routes, not children of
// them — see index.tsx — matching src/action_register/ActionRegister.tsx.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "User Groups",
  companies: "Company Groups",
  permissions: "Permissions",
  dropdowns: "Dropdown Groups",
  "dropdown-configs": "Dropdown Configurations",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Groups of users",
  companies: "Groups of companies",
  permissions: "Module access by group",
  dropdowns: "Groups that fill dropdowns",
  "dropdown-configs": "Group behind each dropdown",
};

/** Default mode is User Groups (list segment). */
export const DEFAULT_MODE_SEGMENT = "list";

/** `<module>.viewMode` per PATTERNS.md's mode-switch storage-key shape. */
export const MODE_STORAGE_KEY = "groups.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota-blocked storage throws on access.
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
